import React, { useState, useEffect, useRef } from 'react';
import { CheckCircle2, Circle, Sparkles, AlertTriangle, CalendarDays, RefreshCcw } from 'lucide-react';
import { generateSchedule } from '../../services/scheduler';
import { PRIORITY_COLORS, PRIORITY_LABELS, SUBJECT_COLORS, SUBJECT_LABELS } from '../../constants';
import api from '../../services/api';

export const Dashboard = ({ weeksAvailable, examDate, intensity, strategy, onReset, onVoltar }) => {
  const [schedule, setSchedule] = useState([]);
  const [completedTopics, setCompletedTopics] = useState(new Set());
  const [showResetModal, setShowResetModal] = useState(false);
  const [hasAccess, setHasAccess] = useState(null); // null = checking, true/false = result
  const [accessCheckError, setAccessCheckError] = useState(null);
  const [showHomeButton, setShowHomeButton] = useState(false);
  const loadingStartTimeRef = useRef(null);

  useEffect(() => {
    // Verificar acesso ao cronograma primeiro
    const checkAccess = async () => {
      try {
        const token = localStorage.getItem('userInfo') ? JSON.parse(localStorage.getItem('userInfo')).token : null;
        if (token) {
          const config = {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          };

          // Verificar acesso ao cronograma
          const response = await api.get('/api/cronograma-personalizado/supporter-status', config);

          if (response.data.hasAccess) {
            setHasAccess(true);
            // Se tiver acesso, carregar o cronograma
            loadCronograma();
          } else {
            setHasAccess(false);
            // Se não tiver acesso, exibir mensagem de erro
            setAccessCheckError({
              message: response.data.message
            });
          }
        } else {
          // Sem token, não é possível verificar
          setHasAccess(false);
          setAccessCheckError({
            message: 'Acesso negado. Você precisa estar logado para acessar o cronograma.'
          });
        }
      } catch (error) {
        console.error('Erro ao verificar acesso:', error);
        // Em caso de erro, tentar carregar o cronograma mesmo assim (fallback)
        setHasAccess(true); // Permitir acesso como fallback
        loadCronograma();
      }
    };

    // Função para carregar o cronograma
    const loadCronograma = async () => {
      try {
        const token = localStorage.getItem('userInfo') ? JSON.parse(localStorage.getItem('userInfo')).token : null;
        if (token) {
          const config = {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          };

          const response = await api.get('/api/cronograma-personalizado', config);

          if (response.data.cronograma) {
            // Atualizar o estado de tópicos concluídos com base no histórico
            const completed = new Set();

            // Adicionar tópicos concluídos do histórico
            if (response.data.cronograma.topicosConcluidos) {
              response.data.cronograma.topicosConcluidos.forEach(topic => {
                if (topic.completed) {
                  completed.add(topic.originalTopicId);
                }
              });
            }

            // Se houver semanas no cronograma, usar e atualizar com base nelas também
            if (response.data.cronograma.semanas && response.data.cronograma.semanas.length > 0) {
              // Usar o cronograma salvo no backend
              setSchedule(response.data.cronograma.semanas);

              // Adicionar tópicos concluídos das semanas atuais
              response.data.cronograma.semanas.forEach(week => {
                week.topics.forEach(topic => {
                  if (topic.completed) {
                    completed.add(topic.originalTopicId);
                  }
                });
              });
            } else {
              // Gerar novo cronograma se não houver semanas
              const generated = generateSchedule(weeksAvailable, intensity, strategy);
              setSchedule(generated);

              // Atualizar o cronograma no backend
              await api.post('/api/cronograma-personalizado', {
                examDate,
                weeksAvailable,
                intensity,
                strategy,
                semanas: generated
              }, config);
            }

            setCompletedTopics(completed);
          } else {
            // Gerar novo cronograma se não houver cronograma salvo
            const generated = generateSchedule(weeksAvailable, intensity, strategy);
            setSchedule(generated);

            // Atualizar o cronograma no backend
            await api.post('/api/cronograma-personalizado', {
              examDate,
              weeksAvailable,
              intensity,
              strategy,
              semanas: generated
            }, config);
          }
        } else {
          // Fallback para geração local se não houver token
          const generated = generateSchedule(weeksAvailable, intensity, strategy);
          setSchedule(generated);
        }
      } catch (error) {
        console.error('Erro ao carregar cronograma do backend:', error);
        // Fallback para geração local em caso de erro
        const generated = generateSchedule(weeksAvailable, intensity, strategy);
        setSchedule(generated);
      }
    };

    checkAccess();
  }, [weeksAvailable, intensity, strategy, examDate]);


  // Efeito para mostrar o botão de voltar após 5 segundos se ainda estiver carregando
  useEffect(() => {
    let timeoutId;

    if (hasAccess === null) { // Ainda carregando
      // Se ainda não temos um tempo de início de carregamento, definimos agora
      if (!loadingStartTimeRef.current) {
        loadingStartTimeRef.current = Date.now();
      }

      // Verificar se já passaram 5 segundos desde o início do carregamento
      const timeElapsed = Date.now() - loadingStartTimeRef.current;
      if (timeElapsed >= 5000) {
        setShowHomeButton(true);
      } else {
        // Configurar timeout para o tempo restante
        const remainingTime = 5000 - timeElapsed;
        timeoutId = setTimeout(() => {
          setShowHomeButton(true);
        }, remainingTime);
      }
    } else {
      // Carregamento terminou, resetar tudo
      setShowHomeButton(false);
      loadingStartTimeRef.current = null; // Resetar o tempo de início
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [hasAccess]);

  // Função para expirar a sessão e voltar para a página inicial
  const expireSessionAndGoHome = () => {
    // Remover informações do usuário do localStorage
    localStorage.removeItem('userInfo');
    localStorage.removeItem('currentPage');

    // Disparar evento de logout para atualizar o estado global
    window.dispatchEvent(new Event('userLoggedOut'));
  };

  // Função para atualizar manualmente o acesso ao cronograma
  const refreshAccessStatus = async () => {
    try {
      const token = localStorage.getItem('userInfo') ? JSON.parse(localStorage.getItem('userInfo')).token : null;
      if (token) {
        const config = {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        };

        // Verificar acesso ao cronograma
        const response = await api.get('/api/cronograma-personalizado/supporter-status', config);

        if (response.data.hasAccess) {
          setHasAccess(true);
          setAccessCheckError(null);
          // Recarregar o cronograma
          loadCronograma();
        } else {
          // Atualizar informações de erro
          setAccessCheckError({
            message: response.data.message
          });
        }
      }
    } catch (error) {
      console.error('Erro ao atualizar acesso:', error);
      setAccessCheckError({
        message: 'Erro ao verificar acesso. Tente novamente mais tarde.'
      });
    }
  };

  // Função auxiliar para recarregar o cronograma
  const loadCronograma = async () => {
    try {
      const token = localStorage.getItem('userInfo') ? JSON.parse(localStorage.getItem('userInfo')).token : null;
      if (token) {
        const config = {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        };

        const response = await api.get('/api/cronograma-personalizado', config);

        if (response.data.cronograma) {
          // Atualizar o estado de tópicos concluídos com base no histórico
          const completed = new Set();

          // Adicionar tópicos concluídos do histórico
          if (response.data.cronograma.topicosConcluidos) {
            response.data.cronograma.topicosConcluidos.forEach(topic => {
              if (topic.completed) {
                completed.add(topic.originalTopicId);
              }
            });
          }

          // Se houver semanas no cronograma, usar e atualizar com base nelas também
          if (response.data.cronograma.semanas && response.data.cronograma.semanas.length > 0) {
            // Usar o cronograma salvo no backend
            setSchedule(response.data.cronograma.semanas);

            // Adicionar tópicos concluídos das semanas atuais
            response.data.cronograma.semanas.forEach(week => {
              week.topics.forEach(topic => {
                if (topic.completed) {
                  completed.add(topic.originalTopicId);
                }
              });
            });
          } else {
            // Gerar novo cronograma se não houver semanas
            const generated = generateSchedule(weeksAvailable, intensity, strategy);
            setSchedule(generated);

            // Atualizar o cronograma no backend
            await api.post('/api/cronograma-personalizado', {
              examDate,
              weeksAvailable,
              intensity,
              strategy,
              semanas: generated
            }, config);
          }

          setCompletedTopics(completed);
        } else {
          // Gerar novo cronograma se não houver cronograma salvo
          const generated = generateSchedule(weeksAvailable, intensity, strategy);
          setSchedule(generated);

          // Atualizar o cronograma no backend
          await api.post('/api/cronograma-personalizado', {
            examDate,
            weeksAvailable,
            intensity,
            strategy,
            semanas: generated
          }, config);
        }
      } else {
        // Fallback para geração local se não houver token
        const generated = generateSchedule(weeksAvailable, intensity, strategy);
        setSchedule(generated);
      }
    } catch (error) {
      console.error('Erro ao carregar cronograma do backend:', error);
      // Fallback para geração local em caso de erro
      const generated = generateSchedule(weeksAvailable, intensity, strategy);
      setSchedule(generated);
    }
  };

  const toggleTopic = async (topicId) => {
    const isCurrentlyCompleted = completedTopics.has(topicId);
    const newCompleted = new Set(completedTopics);

    if (isCurrentlyCompleted) {
      newCompleted.delete(topicId);
    } else {
      newCompleted.add(topicId);
    }

    setCompletedTopics(newCompleted);

    try {
      // Salvar no backend
      const token = localStorage.getItem('userInfo') ? JSON.parse(localStorage.getItem('userInfo')).token : null;
      if (token) {
        const config = {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        };

        // Encontrar a semana e o tópico correspondente para enviar ao backend
        let weekNumber = null;
        for (const week of schedule) {
          const topic = week.topics.find(t => t.originalTopicId === topicId);
          if (topic) {
            weekNumber = week.weekNumber;
            break;
          }
        }

        if (weekNumber) {
          await api.post('/api/cronograma-personalizado/marcar-topico', {
            topicId,
            weekNumber,
            completed: !isCurrentlyCompleted
          }, config);
        } else {
          // Se o tópico não estiver mais no cronograma atual, enviar apenas o ID e o status
          await api.post('/api/cronograma-personalizado/marcar-topico', {
            topicId,
            completed: !isCurrentlyCompleted
          }, config);
        }
      }
    } catch (error) {
      console.error('Erro ao marcar tópico como concluído:', error);
      // Reverter a alteração se houver erro
      setCompletedTopics(prev => {
        const reverted = new Set(prev);
        if (isCurrentlyCompleted) {
          reverted.add(topicId);
        } else {
          reverted.delete(topicId);
        }
        return reverted;
      });
    }
  };

  const calculateProgress = () => {
    let total = 0;
    let done = 0;
    schedule.forEach(week => {
      total += week.topics.length;
      week.topics.forEach(t => {
        if (completedTopics.has(t.originalTopicId)) done++;
      });
    });
    return total === 0 ? 0 : Math.round((done / total) * 100);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Navbar */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => onVoltar('home')}
              className="bg-violet-600 text-white font-semibold px-4 py-2 rounded-lg hover:bg-violet-700 shadow-sm transition-all duration-300"
            >
              Voltar
            </button>
            <div className="flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-violet-800" />
              <h1 className="text-xl font-bold text-gray-900">ENEM Planner</h1>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="hidden md:flex flex-col items-end">
               <span className="text-xs text-gray-500">Prova dia {new Date(examDate).toLocaleDateString('pt-BR')}</span>
               <span className="text-sm font-semibold text-violet-800">{weeksAvailable} semanas restantes</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowResetModal(true)}
                className="p-2 text-gray-500 hover:bg-gray-100 rounded-full"
                title="Reiniciar Planejamento"
              >
                <RefreshCcw className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="h-1 bg-gray-200 w-full">
          <div
            className="h-full bg-violet-800 transition-all duration-500"
            style={{ width: `${calculateProgress()}%` }}
          />
        </div>
      </header>

      <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full">
        <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Seu Cronograma Personalizado</h2>
            <p className="text-gray-600">
                O cronograma organiza todas as matérias misturadas, priorizando conteúdos de <strong>maior incidência</strong>.
            </p>
        </div>

        {schedule.length === 0 ? (
           <div className="text-center py-20">
             <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
             <p className="text-lg text-gray-600">Nenhum cronograma pôde ser gerado para este período.</p>
           </div>
        ) : (
          <div className="space-y-8">
            {schedule.map((week) => (
              <div key={week.weekNumber} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="bg-violet-100 text-violet-800 font-bold px-3 py-1 rounded-lg text-sm">
                      Semana {week.weekNumber}
                    </div>
                    <span className="text-gray-500 text-sm hidden sm:inline-block">
                        {week.topics.length} tópicos para estudar
                    </span>
                  </div>
                  <CalendarDays className="w-5 h-5 text-gray-400" />
                </div>
                
                <div className="divide-y divide-gray-100">
                  {week.topics.map((topic, idx) => {
                    const isCompleted = completedTopics.has(topic.originalTopicId);
                    return (
                      <div 
                        key={`${week.weekNumber}-${idx}`} 
                        className={`group p-4 flex items-center justify-between hover:bg-violet-50/50 transition-colors ${isCompleted ? 'bg-gray-50/80' : ''}`}
                      >
                        <div className="flex items-start gap-4 flex-1">
                          <button 
                            onClick={() => toggleTopic(topic.originalTopicId)}
                            className="mt-1 flex-shrink-0 text-gray-400 hover:text-violet-800 transition-colors"
                          >
                            {isCompleted ? (
                              <CheckCircle2 className="w-6 h-6 text-green-500" />
                            ) : (
                              <Circle className="w-6 h-6" />
                            )}
                          </button>
                          
                          <div className="flex-1">
                            <div className="flex flex-wrap items-center gap-2 mb-1">
                              <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider font-bold ${SUBJECT_COLORS[topic.subject]}`}>
                                {SUBJECT_LABELS[topic.subject]}
                              </span>
                              <h3 className={`font-medium text-lg ${isCompleted ? 'text-gray-400 line-through' : 'text-gray-900 group-hover:text-violet-900'}`}>
                                {topic.name}
                              </h3>
                              <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider font-semibold border ${PRIORITY_COLORS[topic.priority]}`}>
                                {PRIORITY_LABELS[topic.priority]}
                              </span>
                            </div>
                            <p className="text-sm text-gray-500">{topic.category}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Loading Overlay - Shown when checking access */}
      {hasAccess === null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-6 transform transition-all">
            <div className="flex flex-col items-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600 mb-4"></div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Verificando acesso...</h3>
              <p className="text-gray-600 text-center mb-4">
                Aguarde enquanto verificamos seu acesso ao cronograma
              </p>

              {showHomeButton && (
                <button
                  onClick={expireSessionAndGoHome}
                  className="px-4 py-2 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors mt-2"
                >
                  Voltar para a Página Inicial
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Verification Overlay for Users without Access */}
      {hasAccess === false && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-6 transform transition-all">
            <div className="flex flex-col items-center gap-3 mb-4">
              <AlertTriangle className="w-6 h-6 text-amber-500" />
              <h3 className="text-lg font-bold text-gray-900 text-center">Acesso Exclusivo para Usuários Premium</h3>
            </div>

            <p className="text-gray-700 mb-4 text-center">
              Torne-se um usuário premium do Vestibula e tenha acesso ao cronograma completo de estudos
            </p>

            <p className="text-gray-600 text-sm mb-6 text-center">
              Dentro de algumas horas você receberá o seu acesso
            </p>

            <div className="flex flex-col gap-3">
              <button
                onClick={() => window.open('https://vestibula.com.br/venda-premium', '_blank')}
                className="px-4 py-2 bg-violet-600 text-white font-semibold rounded-lg hover:bg-violet-700 transition-colors"
              >
                Torne-se Premium
              </button>
              <button
                onClick={() => onVoltar('home')}
                className="px-4 py-2 text-gray-700 font-medium hover:bg-gray-100 rounded-lg transition-colors"
              >
                Voltar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-6 text-center text-sm text-gray-500">
        <p>Desenvolvido para organizar seus estudos para o ENEM.</p>
      </footer>

      {/* Confirmation Modal */}
      {showResetModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-6 transform transition-all">
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle className="w-6 h-6 text-amber-500" />
              <h3 className="text-lg font-bold text-gray-900">Reiniciar Planejamento</h3>
            </div>

            <p className="text-gray-700 mb-2">
              Tem certeza que deseja reiniciar o planejamento?
            </p>

            <p className="text-gray-600 text-sm mb-6">
              O cronograma atual será apagado, mas as marcações de tópicos concluídos serão mantidas.
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowResetModal(false)}
                className="px-4 py-2 text-gray-700 font-medium hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  onReset();
                  setShowResetModal(false);
                }}
                className="px-4 py-2 bg-violet-600 text-white font-semibold rounded-lg hover:bg-violet-700 transition-colors shadow-sm"
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};